<script setup>
  import moment from "moment";
  import { computed, onMounted, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter, useRoute } from "vue-router";
  import { useDebounceFn } from "@vueuse/core";
  import { File, SquareArrowOutUpRight } from "lucide-vue-next";
  import ThreePanePage from "@/templates/ThreePanePage.vue";

  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { FormItem } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { AiTextInput } from "@/components/episto-ui/ai";
  import { Select } from "@/components/episto-ui/select";
  import { RadioGroup, RadioGroupItemIcon } from "@/components/ui/radio-group";
  import { Badge } from "@/components/ui/badge";

  import { usePricerProjectStore } from "@stores/pricer/pricerProject";
  import { usePricerHubSpotStore } from "@stores/pricer/pricerHubSpot";
  import { useUsersStore } from "@stores/users";

  const router = useRouter();
  const route = useRoute();
  const pricerProjectStore = usePricerProjectStore();
  const hubSpotStore = usePricerHubSpotStore();
  const usersStore = useUsersStore();
  const { project, isNewProject, designOptions, isAiProject, projectHasMissingParams } =
    storeToRefs(pricerProjectStore);
  const { companiesOptions, isLoadingCompanies, contactsOptions, isLoadingContacts } = storeToRefs(hubSpotStore);
  const { adminSalesOptions } = storeToRefs(usersStore);
  const { initProject, saveProject } = pricerProjectStore;
  const {
    searchCompanies,
    searchContacts,
    clearContacts,
    fetchContacts,
    getCompanyById,
    getContactById,
    getContactFullName,
    ensureProjectSelectionInList,
  } = hubSpotStore;

  const hasSelectedCompany = computed(() => !!project.value.company_id);

  const handleCompanySearch = useDebounceFn((query) => {
    searchCompanies(query);
  }, 300);

  const handleContactSearch = useDebounceFn((query) => {
    if (!project.value.company_id) return;
    searchContacts(project.value.company_id, query);
  }, 300);

  // Update client name when company is selected
  watch(
    () => project.value.company_id,
    (newCompanyId, oldCompanyId) => {
      if (newCompanyId !== oldCompanyId) {
        // Update client name from selected company
        const company = getCompanyById(newCompanyId);
        if (company) {
          project.value.client = company.name;
        }
        // Clear contact when company changes
        project.value.contact_id = null;
        project.value.contact_name = null;
        clearContacts();
        // Fetch initial contacts for the new company
        if (newCompanyId) {
          fetchContacts(newCompanyId);
        }
      }
    },
  );

  // Update contact_name when contact is selected
  watch(
    () => project.value.contact_id,
    (newContactId) => {
      if (newContactId) {
        const contact = getContactById(newContactId);
        if (contact) {
          project.value.contact_name = getContactFullName(contact);
        }
      } else {
        project.value.contact_name = null;
      }
    },
  );

  const languageOptions = ref([
    { label: "French", value: "fr" },
    { label: "English", value: "en" },
  ]);
  const askLicenceOptions = ref([
    { label: "Yes", value: true },
    { label: "No", value: false },
  ]);
  const projectTitle = computed(() => (isNewProject.value ? "New project" : "Project settings"));
  const createdAtLabel = computed(() =>
    project.value.created_at ? moment(project.value.created_at).format("DD/MM/YYYY") : "--",
  );

  const getImageUrl = (icon) => {
    return new URL(`/src/assets/images/icons/${icon}`, import.meta.url).href;
  };

  const onSubmit = async () => {
    await saveProject();
    if (isAiProject.value) {
      router.push({ name: "PricerProjectsIndex" });
    } else {
      router.push({ name: "PricerProjectTargets", params: { token: project.value.token } });
    }
  };

  onMounted(() => {
    if (route.name === "PricerProjectNew") {
      initProject();
    } else {
      ensureProjectSelectionInList();
    }
  });
</script>

<template>
  <ThreePanePage :span-center="6">
    <template #center>
      <Card class="p-6">
        <CardHeader class="pb-6">
          <CardTitle class="heading-page">{{ projectTitle }}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-6">
          <form
            class="space-y-6"
            @submit.prevent="onSubmit">
            <FormItem>
              <Label for="name">Deal name <span class="text-destructive">*</span></Label>
              <Input
                id="name"
                v-model="project.name"
                required />
            </FormItem>
            <FormItem>
              <Label for="client">Client <span class="text-destructive">*</span></Label>
              <Select
                v-model="project.company_id"
                :options="companiesOptions"
                :loading="isLoadingCompanies"
                placeholder="Search for a company..."
                filterable
                remote
                @search="handleCompanySearch" />
            </FormItem>
            <FormItem v-if="hasSelectedCompany">
              <Label for="contact">Contact <span class="text-destructive">*</span></Label>
              <Select
                v-model="project.contact_id"
                :options="contactsOptions"
                :loading="isLoadingContacts"
                placeholder="Search for a contact..."
                filterable
                remote
                @search="handleContactSearch" />
            </FormItem>
            <FormItem>
              <Label for="end_client">Final client</Label>
              <Input
                id="end_client"
                v-model="project.end_client" />
            </FormItem>
            <div
              v-if="!isNewProject"
              class="grid grid-cols-2 gap-4">
              <FormItem>
                <Label>Date</Label>
                <div class="text-sm text-muted-foreground">
                  {{ createdAtLabel }}
                </div>
              </FormItem>
              <FormItem>
                <Label>Pricing version</Label>
                <div>
                  <Badge
                    :class="
                      project.has_latest_pricing_version
                        ? 'bg-status-success-soft text-status-success-strong hover:bg-status-success-soft'
                        : 'bg-status-info-soft text-status-info-strong hover:bg-status-info-soft'
                    ">
                    {{ project.version_name || "--" }}
                  </Badge>
                </div>
              </FormItem>
            </div>
            <FormItem v-if="!isNewProject">
              <Label for="owner">Owner <span class="text-destructive">*</span></Label>
              <Select
                v-model="project.owner_id"
                :options="adminSalesOptions"
                placeholder="Select owner"
                filterable />
            </FormItem>
            <FormItem v-if="project.crm_id">
              <Label for="crm_id">CRM ID</Label>
              <div class="flex items-center gap-2">
                <Input
                  id="crm_id"
                  :model-value="project.crm_id"
                  disabled
                  class="flex-1" />
                <Button
                  variant="ghost"
                  size="icon"
                  as="a"
                  :href="`https://app.hubspot.com/contacts/9187193/record/0-3/${project.crm_id}`"
                  target="_blank"
                  rel="noopener noreferrer">
                  <SquareArrowOutUpRight class="h-4 w-4" />
                </Button>
              </div>
            </FormItem>
            <FormItem>
              <Label for="language">Language <span class="text-destructive">*</span></Label>
              <Select
                v-model="project.hs_locale"
                :options="languageOptions"
                placeholder="Select language" />
            </FormItem>
            <FormItem>
              <Label for="has_ask_licence">Client has Ask Licence <span class="text-destructive">*</span></Label>
              <Select
                v-model="project.has_ask_licence"
                :options="askLicenceOptions"
                placeholder="Select..." />
            </FormItem>
            <FormItem v-if="isNewProject">
              <Label>Design mode <span class="text-destructive">*</span></Label>
              <RadioGroup
                v-model="project.creation_mode"
                class="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <RadioGroupItemIcon
                  v-for="option in designOptions"
                  :key="option.value"
                  :value="`${option.value}`"
                  :label="option.label"
                  class="h-full">
                  <template #icon>
                    <File
                      v-if="option.icon === 'file'"
                      class="text-black" />
                    <img
                      v-else
                      :src="getImageUrl(option.custom_icon)" />
                  </template>
                </RadioGroupItemIcon>
              </RadioGroup>
            </FormItem>
            <FormItem v-if="!isNewProject || !isAiProject">
              <Label for="survey_topic">Survey topic <span class="text-destructive">*</span></Label>
              <AiTextInput
                id="survey_topic"
                v-model="project.survey_topic"
                type="textarea"
                :rows="3" />
            </FormItem>
            <FormItem>
              <Label for="brief">
                Brief
                <span
                  v-if="isAiProject"
                  class="text-destructive"
                  >*</span
                >
              </Label>
              <AiTextInput
                id="brief"
                v-model="project.brief"
                type="textarea"
                :rows="4" />
            </FormItem>
            <Button
              v-if="isNewProject"
              type="submit"
              class="w-full"
              :disabled="projectHasMissingParams">
              OK
            </Button>
          </form>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
